// Phase 10A — given a seed accent HSL triplet, generate the four
// CSS custom properties needed for full accent coverage:
//
//   --color-accent       — the base
//   --color-accent-hover — darker variant for interactive states
//   --color-accent-soft  — desaturated lighter background tint
//   --color-on-accent    — ink color that sits ON the accent (text on
//                          a primary-accent button)
//
// The algebra below was fit to the existing hand-tuned tokens.css
// "Copper" preset so that selecting `copper` produces values within
// 4% of every existing channel (effectively a visual no-op for
// existing users). Other hues then flow through the same rules.
//
// This file is the math layer ONLY. The presets file (themePresets.ts)
// uses these formulas to pre-compute swatches at module load time; the
// ThemeProvider hooks the result onto document.documentElement.

import type { HSL } from './contrastRatio';

export type ResolvedMode = 'light' | 'dark';

export interface AccentPaletteTokens {
  '--color-accent': string;
  '--color-accent-hover': string;
  '--color-accent-soft': string;
  '--color-on-accent': string;
}

/**
 * Format an HSL triplet to a CSS hsl() string. Decimals are rounded so
 * the inline style block stays compact and diff-friendly.
 */
function fmt(h: number, s: number, l: number): string {
  const hr = Math.round(((h % 360) + 360) % 360);
  const sr = Math.round(Math.max(0, Math.min(100, s)));
  const lr = Math.round(Math.max(0, Math.min(100, l)));
  return `hsl(${hr} ${sr}% ${lr}%)`;
}

/**
 * Derive the full accent palette for the active mode from a seed HSL.
 * `seed` is the user-chosen "base" accent (e.g. Copper = hsl(18 58 44)).
 *
 * The seed itself is interpreted differently for light vs dark — that
 * lets the same preset key (e.g. "copper") emit a light triple and a
 * separate dark triple via two seed values. See themePresets.ts.
 *
 * Algorithm:
 *   light:
 *     base   = seed
 *     hover  = seed with L - 6 (clamped 0..100)
 *     soft   = seed with S - 8 (min 0), L + 48 (max 92)
 *     on     = L<=50 ? inverted-ink : primary-ink
 *
 *   dark:  (seed is the dark-mode base, already lightness-shifted)
 *     base   = seed
 *     hover  = seed with L + 6, clamped to 0..90 (lighter on hover)
 *     soft   = seed with S - 40 (min 15), L = 18 (deep tinted background)
 *     on     = dark canvas ink (matches existing tokens.css)
 *
 * Fitted so copper (h=18, s=58, l=44) produces:
 *   base  = hsl(18 58% 44%)   (existing)
 *   hover = hsl(18 58% 38%)   (existing was 62% — 4% sat shift, imperceptible)
 *   soft  = hsl(18 50% 92%)   (existing)
 */
export function paletteFromAccent(
  seed: HSL,
  mode: ResolvedMode,
): AccentPaletteTokens {
  const { h, s, l } = seed;
  if (mode === 'light') {
    return {
      '--color-accent': fmt(h, s, l),
      '--color-accent-hover': fmt(h, s, l - 6),
      '--color-accent-soft': fmt(h, Math.max(0, s - 8), Math.min(92, l + 48)),
      // Light mode: dark accents (L<=50) take the warm-paper ink-inverted
      // (matches existing --color-ink-inverted: hsl(36 35% 96%));
      // pale accents (L>50) take the espresso primary ink
      // (--color-ink-primary: hsl(20 18% 14%)).
      '--color-on-accent': l <= 50 ? 'hsl(36 35% 96%)' : 'hsl(20 18% 14%)',
    };
  }
  // dark
  return {
    '--color-accent': fmt(h, s, l),
    '--color-accent-hover': fmt(h, s, Math.min(90, l + 6)),
    '--color-accent-soft': fmt(h, Math.max(15, s - 40), 18),
    // Dark mode: text-on-accent reads against the now-lighter accent;
    // espresso-on-warm reads cleanly. Mirrors existing
    // --color-on-accent: hsl(280 12% 8%) in [data-theme='dark'].
    '--color-on-accent': 'hsl(280 12% 8%)',
  };
}

/**
 * Convenience: build an inline-style string from the palette tokens.
 * Currently unused (we set props directly via style.setProperty), but
 * exported for tests + potential SSR / fallback paths.
 */
export function paletteToStyleString(p: AccentPaletteTokens): string {
  return Object.entries(p)
    .map(([k, v]) => `${k}: ${v};`)
    .join(' ');
}
